// 老師「整頁修訂模式」編輯 AI 批改結果(取代逐欄位就地編輯版)
// 互動:工具列「修改評語」鈕(與上一位/下一位/返回同排)→ 進入修訂模式，所有可編輯欄位變成虛線框可直接編輯
// → 工具列同排變成「取消／儲存」→ 儲存一次送出所有變更欄位(單一 PUT、只帶有變更的欄位)
import { useEffect, type ReactNode } from "react";

// correctingResult JSON 的單一區塊:評分項用 得分/評語/等第,整體評分用 總分/滿分/評語/建議/等第
export interface CorrectingResultEntry {
  得分?: number | string;
  評語?: string;
  等第?: string;
  總分?: number | string;
  滿分?: number;
  建議?: string[];
}

export type CorrectingResultJson = Record<string, CorrectingResultEntry | undefined>;

// 修訂模式草稿:進入修訂時從畫面資料初始化，儲存時 diff 出變更欄位
export interface RevisionDraft {
  total: string;
  overall: string;
  suggestions: string[];
  catScores: Record<string, string>;
  catComments: Record<string, string>;
}

export const PencilIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
  </svg>
);

// 虛線可編輯欄位樣式(預想畫面元素、配色走站內 token):
// 框線用 --color-secondary #2781E2(站內 focus outline 同色)，focus 變實線＋同色光暈
// (不含寬度:分數欄要窄框、文字欄要滿版，由各自元件補)
const dashedFieldClass =
  "rounded-md border-[1.5px] border-dashed border-[#2781E2] bg-[#F7FBFF] outline-none " +
  "transition-[border-color,box-shadow] focus:border-solid focus:border-[#2781E2] " +
  "focus:shadow-[0_0_0_3px_rgba(39,129,226,0.15)]";

const FieldError = ({ message }: { message: string }) => (
  <p className="mt-1 text-sm text-red-600 font-normal" role="alert">
    {message}
  </p>
);

interface RevisionScoreInputProps {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  max?: number;
  error?: string;
  disabled?: boolean;
}

// 分數欄位(總分/各項得分):數字輸入＋「/ 滿分」提示＋就地錯誤標示
export const RevisionScoreInput = ({
  value,
  onChange,
  ariaLabel,
  max,
  error,
  disabled = false,
}: RevisionScoreInputProps) => (
  <div>
    <div className="flex items-center gap-2">
      <input
        type="number"
        min={0}
        max={max}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
        className={`${dashedFieldClass} w-24 px-3 py-1.5 text-right text-base font-normal text-gray-800`}
      />
      {typeof max === "number" && max > 0 && (
        <span className="text-gray-500 text-sm font-normal">/ {max}分</span>
      )}
    </div>
    {error && <FieldError message={error} />}
  </div>
);

interface RevisionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  rows?: number;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
}

// 文字欄位(各項評語/整體評語/建議):虛線 textarea＋就地錯誤標示
export const RevisionTextarea = ({
  value,
  onChange,
  ariaLabel,
  rows = 4,
  placeholder,
  error,
  disabled = false,
}: RevisionTextareaProps) => {
  // 高度自動跟內容走，不出現內部滾動條（掛載與每次輸入都重算）
  const autoResize = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  };
  return (
    <div>
      <textarea
        value={value}
        rows={rows}
        disabled={disabled}
        ref={autoResize}
        onChange={(e) => {
          autoResize(e.target);
          onChange(e.target.value);
        }}
        aria-label={ariaLabel}
        placeholder={placeholder}
        className={`${dashedFieldClass} w-full resize-none overflow-hidden px-4 py-3 text-sm text-gray-700 font-normal leading-relaxed`}
      />
      {error && <FieldError message={error} />}
    </div>
  );
};

// 工具列內聯「修改評語」鈕:與上一位/下一位/返回同排、同尺寸感(比照返回鈕的
// px-3/py-1/md:py-2.5/md:px-4/md:text-lg/h-[30px]/md:h-12 樣式)。
// 手機寬度有限(工具列易超版)，md 以下只留 icon，文字收起；aria-label/title 仍在,
// 不影響可及性。配色走站內主色 #254D71(頁首/主要按鈕同色)，字級跟站內按鈕 font-medium
export const RevisionEditButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    title="修改評語：進入後可一次修改所有分數與評語，儲存才會生效"
    aria-label="修改評語"
    data-testid="revision-edit"
    className="flex items-center justify-center gap-1.5 px-3 py-1 md:py-2.5 md:px-4 md:text-lg text-white font-medium bg-[#254D71] rounded-md h-[30px] md:h-12 transition-colors hover:bg-[#254D71]/90"
  >
    <PencilIcon className="h-4 w-4 md:h-5 md:w-5 shrink-0" />
    <span className="hidden md:inline">修改評語</span>
  </button>
);

interface RevisionActionBarProps {
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  saveDisabled: boolean;
}

// 修訂模式工具列內聯「取消／儲存」雙鈕(與上一位/下一位/返回同排)
// 樣式對齊站內按鈕:主色 #254D71 實心/外框、rounded-md、font-medium，尺寸比照返回鈕
// (儲存失敗改走站內自製 RevisionPopup 錯誤彈窗，不在此顯示)
// 注意:不含底部漸層提示條,那是頁面層級裝飾、與按鈕位置無關,改由 RevisionFoot
// 獨立元件在頁面層級掛載(見 Feedback.tsx),不跟這兩顆按鈕綁在一起。
// 這裡直接回傳 Fragment,兩顆按鈕會自然吃到父層工具列容器本身的間距設定,
// 不需要自己額外包一層 div 造成間距跟旁邊「返回」鈕不一致。
export const RevisionActionBar = ({
  onCancel,
  onSave,
  saving,
  saveDisabled,
}: RevisionActionBarProps) => (
  <>
    <button
      type="button"
      onClick={onCancel}
      disabled={saving}
      data-testid="revision-cancel"
      className="rounded-md border border-[#254D71] bg-white px-3 py-1 md:py-2.5 md:px-4 md:text-lg font-medium text-[#254D71] h-[30px] md:h-12 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
    >
      取消
    </button>
    <button
      type="button"
      onClick={onSave}
      disabled={saving || saveDisabled}
      data-testid="revision-save"
      className="flex items-center gap-2 rounded-md bg-[#254D71] px-3 py-1 md:py-2.5 md:px-4 md:text-lg font-medium text-white h-[30px] md:h-12 transition-colors hover:bg-[#254D71]/90 disabled:cursor-not-allowed disabled:bg-gray-400 disabled:shadow-none"
    >
      {saving ? "儲存中..." : "儲存"}
    </button>
  </>
);

// 修訂模式底部漸層提示條(頁面層級視覺提示，與按鈕位置無關，維持原樣不動;
// 跟隨預想畫面;配色走站內主色 #254D71 → 輔色 #1CCAC6)。
// 從 RevisionActionBar 拆出來獨立掛載，理由見上方註解。
export const RevisionFoot = () => (
  <div
    data-testid="revision-foot"
    className="fixed inset-x-0 bottom-0 z-30 h-[5px] bg-gradient-to-r from-[#254D71] via-[#1CCAC6] to-[#254D71]"
  />
);

interface RevisionPopupProps {
  message: string;
  onClose: () => void; // ✕／取消／訊息型的確認鈕都走這裡
  onConfirm?: () => void; // 有給 → 確認型:顯示「取消／確認」雙鈕
  autoCloseMs?: number; // 訊息型自動關閉毫秒數(仍可手動按確認提早關)
}

// 修訂模式站內自製彈窗(視覺完全對齊 UploadEssay 的 ErrorPopup):
// 白色半透明遮罩置中、白卡、深藍 #254D71 標題列「提示」＋右上 ✕、
// 內文 info icon(#2D86FF)＋灰字訊息、footer 確認鈕(確認型多一顆外框取消鈕)
export const RevisionPopup = ({
  message,
  onClose,
  onConfirm,
  autoCloseMs,
}: RevisionPopupProps) => {
  useEffect(() => {
    if (!autoCloseMs) return;
    const timer = setTimeout(onClose, autoCloseMs);
    return () => clearTimeout(timer);
    // onClose 每次 render 都是新函式，依 autoCloseMs 掛一次即可
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCloseMs]);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ backgroundColor: "rgba(255, 255, 255, 0.75)" }}
      data-testid="revision-popup"
    >
      <div className="bg-white rounded-lg shadow-md max-w-md w-full mx-4 overflow-hidden border border-gray-200">
        <div className="bg-[#254D71] px-4 py-3 text-white flex justify-between items-center">
          <h3 className="font-medium text-lg">提示</h3>
          <button onClick={onClose} className="text-white hover:text-gray-200" aria-label="關閉">
            <svg
              className="h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-6 w-6 text-[#2D86FF]"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-gray-700 whitespace-pre-line">{message}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          {onConfirm ? (
            <>
              <button
                type="button"
                onClick={onConfirm}
                data-testid="revision-popup-confirm"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#2D86FF] text-base font-medium text-white hover:bg-blue-600 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
              >
                確認
              </button>
              <button
                type="button"
                onClick={onClose}
                data-testid="revision-popup-cancel"
                className="mt-3 w-full inline-flex justify-center rounded-md border border-[#254D71] shadow-sm px-4 py-2 bg-white text-base font-medium text-[#254D71] hover:bg-gray-50 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
              >
                取消
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onClose}
              data-testid="revision-popup-confirm"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#2D86FF] text-base font-medium text-white hover:bg-blue-600 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
            >
              確認
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// 修訂模式中包住可編輯欄位的容器(方便測試/樣式定位)
export const RevisionFieldWrap = ({
  fieldId,
  children,
}: {
  fieldId: string;
  children: ReactNode;
}) => <div data-revision-field={fieldId}>{children}</div>;
